import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Button, Grid, IconButton, Stack, TextField, Typography, Chip, Card,
    Accordion, AccordionSummary, AccordionDetails, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon, Download as DownloadIcon, Visibility as VisibilityIcon,
    Add as AddIcon, CameraAlt as CameraIcon, UploadFile as UploadFileIcon,
    Mic as MicIcon, Stop as StopIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { initializeDB } from '../../../config/db';
import { TOOL_DOCS_STORE } from '../../../config/constants';
import useSftpDownload from './useSftpDownload';
import PdfPreviewModal from '../../jha/pdf/PdfPreviewModal';
import CameraCapture from '../../shared/CameraCapture';
import axiosInstance from '../../../axiosInstance';
import Swal from 'sweetalert2';
import useDictation from '../../../hooks/useDictation';

const accordionSx = { mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } };
const accordionSummarySx = { minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } };
const sectionTitle = (text) => <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{text}</Typography>;

function getCalDueDate(toolCals, calIntervalMonths) {
    if (!toolCals || toolCals.length === 0) return null;
    const sorted = [...toolCals].sort((a, b) => (b.calDate || '').localeCompare(a.calDate || ''));
    const latest = sorted[0];
    if (latest.calNextDate) return latest.calNextDate;
    if (latest.calDate && calIntervalMonths) {
        const d = new Date(latest.calDate);
        d.setMonth(d.getMonth() + Math.round(calIntervalMonths));
        return dayjs(d).format('YYYY-MM-DD'); // local day (not UTC)
    }
    return null;
}

export default function CalibrationView({ filteredTools, allCalibrations }) {
    const { docLoading, handleDownload } = useSftpDownload();
    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({ lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000, prime: true });

    const toggleNotesDictation = async () => {
        if (!dictationSupported) return;
        if (isListening) { stopDictation(); return; }
        await startDictation({
            onResult: (transcript) => {
                if (!transcript) return;
                setUploadData(p => {
                    const cur = p.calNotes || '';
                    const space = cur && !cur.endsWith(' ') ? ' ' : '';
                    return { ...p, calNotes: cur + space + transcript };
                });
            },
        });
    };
    const [calSelectedPart, setCalSelectedPart] = useState(null);
    const [toolDocs, setToolDocs] = useState([]);
    // PDF Preview Modal
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('');
    const [pdfLoading, setPdfLoading] = useState(false);
    // Upload form
    const [uploadOpen, setUploadOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [uploadData, setUploadData] = useState({
        calDate: dayjs().format('YYYY-MM-DD'),
        calWho: localStorage.getItem('employeeFullName') || '',
        calNotes: '',
        photos: [],       // captured photos [{dataUrl, name, timestamp}]
        pdfFile: null,     // uploaded PDF File object
        pdfDataUrl: null,  // base64 of uploaded PDF
    });
    const [uploading, setUploading] = useState(false);

    const calToolsTable = useMemo(() => {
        return filteredTools.map(tool => {
            const toolCals = allCalibrations.filter(c => c.part === tool.part)
                .sort((a, b) => (b.calDate || '').localeCompare(a.calDate || ''));
            return {
                ...tool,
                calDueDate: getCalDueDate(toolCals, tool.calInterval),
                lastCalDate: toolCals[0]?.calDate || null,
                calCount: toolCals.length
            };
        }).sort((a, b) => {
            if (!a.calDueDate && !b.calDueDate) return 0;
            if (!a.calDueDate) return 1;
            if (!b.calDueDate) return -1;
            return a.calDueDate.localeCompare(b.calDueDate);
        });
    }, [filteredTools, allCalibrations]);

    const calSelectedTool = useMemo(() => calSelectedPart ? calToolsTable.find(t => t.part === calSelectedPart) : null, [calSelectedPart, calToolsTable]);

    const calSelectedHistory = useMemo(() => {
        if (!calSelectedPart) return [];
        return allCalibrations.filter(c => c.part === calSelectedPart)
            .sort((a, b) => (b.calDate || '').localeCompare(a.calDate || ''))
            .slice(0, 3);
    }, [calSelectedPart, allCalibrations]);

    useEffect(() => {
        if (!calSelectedPart) { setToolDocs([]); return; }
        (async () => {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(TOOL_DOCS_STORE)) {
                const tx = db.transaction(TOOL_DOCS_STORE, 'readonly');
                setToolDocs(await tx.objectStore(TOOL_DOCS_STORE).index('part').getAll(calSelectedPart) || []);
            }
        })();
    }, [calSelectedPart]);

    // View CalDoc PDF inline
    const handleViewPdf = async (sftpPath, docId) => {
        setPdfLoading(true);
        setPdfPreviewOpen(true);
        setPdfFilename(sftpPath.split(/[/\\]/).pop() || 'certificate.pdf');
        const blob = await handleDownload(sftpPath, docId, 'blob');
        if (blob) setPdfBlob(blob);
        setPdfLoading(false);
    };

    // Режим: 'pdf' или 'photo' — одно из двух
    const hasFile = !!uploadData.pdfDataUrl || uploadData.photos.length > 0;
    const fileMode = uploadData.pdfDataUrl ? 'pdf' : uploadData.photos.length > 0 ? 'photo' : null;

    // Camera capture — только если нет PDF
    const handlePhotoCapture = (photoData) => {
        setUploadData(prev => ({
            ...prev,
            photos: [...prev.photos, { dataUrl: photoData.dataUrl, name: photoData.name, timestamp: photoData.timestamp }],
            pdfFile: null, pdfDataUrl: null // сбрасываем PDF
        }));
        setCameraOpen(false);
    };

    // PDF file upload — только если нет фото
    const handlePdfFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setUploadData(prev => ({ ...prev, pdfFile: file, pdfDataUrl: reader.result, photos: [] })); // сбрасываем фото
        };
        reader.readAsDataURL(file);
    };

    const removePhoto = (idx) => {
        setUploadData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));
    };

    const removePdf = () => {
        setUploadData(prev => ({ ...prev, pdfFile: null, pdfDataUrl: null }));
    };

    // Reset upload form
    const resetUpload = () => {
        setUploadData({
            calDate: dayjs().format('YYYY-MM-DD'), calWho: localStorage.getItem('employeeFullName') || '',
            calNotes: '', photos: [], pdfFile: null, pdfDataUrl: null,
        });
        setUploadOpen(false);
    };

    // Submit calibration
    const handleSubmitCalibration = async () => {
        if (!calSelectedPart) return;
        if (!uploadData.calDate) { Swal.fire({ icon: 'error', title: 'Error', text: 'Calibration date is required' }); return; }
        if (uploadData.photos.length === 0 && !uploadData.pdfDataUrl) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Upload a PDF or take a photo' }); return;
        }
        try {
            setUploading(true);
            const payload = {
                part: calSelectedPart,
                calDate: uploadData.calDate,
                calWho: uploadData.calWho,
                calNotes: uploadData.calNotes,
                calInterval: calSelectedTool?.calInterval || null,
                photos: uploadData.photos.map(p => ({ dataUrl: p.dataUrl, name: p.name })),
                pdfDataUrl: uploadData.pdfDataUrl || null,
                pdfFileName: uploadData.pdfFile?.name || null,
            };
            await axiosInstance.post('/gas-monitor-test/calibration-upload', payload);
            Swal.fire({ icon: 'success', title: 'Success!', text: 'Calibration record uploaded' });
            resetUpload();
        } catch (error) {
            console.error('Error uploading calibration:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to upload calibration' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            {/* Gas Monitor Calibration Table */}
            <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                    {sectionTitle(`Gas Monitors — Calibration Status (${calToolsTable.length})`)}
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Tool #</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Serial No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Model</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Cal Due</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {calToolsTable.map(tool => {
                                    const isOverdue = tool.calDueDate && new Date(tool.calDueDate) < new Date();
                                    const isSelected = calSelectedPart === tool.part;
                                    return (
                                        <TableRow key={tool.id} hover selected={isSelected}
                                            onClick={() => setCalSelectedPart(tool.part)}
                                            sx={{ cursor: 'pointer', '&.Mui-selected': { backgroundColor: '#e3f2fd' } }}>
                                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{tool.part}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>{tool.serialNo || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>{tool.model || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>
                                                <Chip label={tool.inventoryStatus || '—'} size="small" sx={{
                                                    height: 18, fontSize: '0.6rem',
                                                    backgroundColor: tool.inventoryStatus === 'Available' ? '#4caf50' : tool.inventoryStatus === 'Assigned' ? '#2196f3' :
                                                        tool.inventoryStatus === 'Missing' ? '#f44336' : tool.inventoryStatus === 'Out of Service' ? '#9e9e9e' :
                                                        tool.inventoryStatus === 'Sent for Calibration' ? '#ff9800' : '#757575', color: '#fff'
                                                }} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: isOverdue ? 700 : 400, color: isOverdue ? '#f44336' : 'inherit' }}>
                                                {tool.calDueDate || '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Button size="small" variant={isSelected ? 'contained' : 'outlined'}
                                                    sx={{ fontSize: '0.65rem', minWidth: 50, py: 0 }}
                                                    onClick={(e) => { e.stopPropagation(); setCalSelectedPart(tool.part); }}>View</Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>

            {/* Selected Tool Details */}
            {calSelectedTool && (
                <>
                    {/* Tool Info */}
                    <Accordion defaultExpanded sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            {sectionTitle(`Tool: ${calSelectedTool.part} — ${calSelectedTool.serialNo || ''}`)}
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={1}>
                                <Grid item xs={6} sm={3}><TextField label="Part" size="small" fullWidth value={calSelectedTool.part || ''} disabled InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={6} sm={3}><TextField label="Serial No" size="small" fullWidth value={calSelectedTool.serialNo || ''} disabled InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={6} sm={3}><TextField label="Model" size="small" fullWidth value={calSelectedTool.model || ''} disabled InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={6} sm={3}><TextField label="Manufacturer" size="small" fullWidth value={calSelectedTool.manufacturer || ''} disabled InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={6} sm={3}><TextField label="Status" size="small" fullWidth value={calSelectedTool.inventoryStatus || ''} disabled InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={6} sm={3}><TextField label="Cal Interval (months)" size="small" fullWidth value={calSelectedTool.calInterval || ''} disabled InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={6} sm={3}>
                                    {(() => {
                                        const dueDate = calSelectedTool.calDueDate;
                                        const isOverdue = dueDate && new Date(dueDate) < new Date();
                                        return <TextField label="Calibration Due" size="small" fullWidth value={dueDate || 'N/A'} disabled
                                            InputLabelProps={{ shrink: true }}
                                            sx={isOverdue ? { '& .MuiInputBase-input': { color: '#f44336', fontWeight: 700 } } : {}} />;
                                    })()}
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Upload Calibration (выше History) */}
                    <Accordion expanded={uploadOpen} onChange={() => setUploadOpen(!uploadOpen)} sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AddIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                                {sectionTitle('Upload Calibration')}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1.5} columnSpacing={1}>
                                <Grid item xs={6}>
                                    <TextField label="Calibration Date *" type="date" size="small" fullWidth
                                        value={uploadData.calDate} onChange={e => setUploadData(p => ({ ...p, calDate: e.target.value }))}
                                        InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField label="Calibrated By" size="small" fullWidth
                                        value={uploadData.calWho} onChange={e => setUploadData(p => ({ ...p, calWho: e.target.value }))} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ position: 'relative' }}>
                                        <TextField label="Notes" size="small" fullWidth multiline rows={2}
                                            value={uploadData.calNotes} onChange={e => setUploadData(p => ({ ...p, calNotes: e.target.value }))} />
                                        {dictationSupported && (
                                            <IconButton onClick={toggleNotesDictation} color={isListening ? 'error' : 'primary'}
                                                size="small" sx={{ position: 'absolute', right: 4, top: 4 }}>
                                                {isListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>

                                {/* PDF or Photo — одно из двух */}
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        {hasFile
                                            ? (fileMode === 'pdf' ? 'PDF attached. Remove to switch to photo.' : 'Photo(s) attached. Remove to switch to PDF.')
                                            : 'Attach calibration certificate — PDF or photo:'}
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button variant="outlined" startIcon={<UploadFileIcon />} component="label" size="small"
                                            disabled={fileMode === 'photo'}>
                                            Upload PDF
                                            <input type="file" hidden accept=".pdf" onChange={handlePdfFileChange} />
                                        </Button>
                                        <Button variant="outlined" startIcon={<CameraIcon />} size="small"
                                            disabled={fileMode === 'pdf'}
                                            onClick={() => setCameraOpen(true)}>
                                            Take Photo
                                        </Button>
                                    </Stack>
                                </Grid>

                                {/* Uploaded PDF */}
                                {uploadData.pdfFile && (
                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip label="PDF" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#e53935', color: '#fff' }} />
                                                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }} noWrap>{uploadData.pdfFile.name}</Typography>
                                                <Button size="small" color="error" onClick={removePdf}>Remove</Button>
                                            </Box>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Captured photos */}
                                {uploadData.photos.length > 0 && (
                                    <Grid item xs={12}>
                                        <Stack spacing={0.5}>
                                            {uploadData.photos.map((photo, idx) => (
                                                <Card key={idx} variant="outlined" sx={{ p: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 50, height: 50, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                                                            <img src={photo.dataUrl} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        </Box>
                                                        <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }} noWrap>{photo.name}</Typography>
                                                        <Button size="small" color="error" onClick={() => removePhoto(idx)}>Remove</Button>
                                                    </Box>
                                                </Card>
                                            ))}
                                        </Stack>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Button variant="contained" fullWidth disabled={uploading || !hasFile} onClick={handleSubmitCalibration}
                                        sx={{ mt: 1, backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>
                                        {uploading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Upload Calibration'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Calibration History */}
                    <Accordion defaultExpanded sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            {sectionTitle(`Calibration History (${calSelectedHistory.length})`)}
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
                            {calSelectedHistory.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No calibration records</Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Cal Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Cal Who</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Next Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Notes</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Certificate</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {calSelectedHistory.map((cal, idx) => {
                                                const isOverdue = cal.calNextDate && new Date(cal.calNextDate) < new Date();
                                                const hasDoc = !!cal.calDoc;
                                                const docName = hasDoc ? cal.calDoc.split(/[/\\]/).pop() : null;
                                                const isPdf = docName?.toLowerCase().endsWith('.pdf');
                                                return (
                                                    <TableRow key={cal.id || idx}>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>{cal.calDate || '—'}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem' }}>{cal.calWho || '—'}</TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem', color: isOverdue ? '#f44336' : 'inherit', fontWeight: isOverdue ? 600 : 400 }}>
                                                            {cal.calNextDate || '—'}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 120 }}>
                                                            <Typography variant="caption" noWrap>{cal.calNotes || '—'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {hasDoc ? (
                                                                <Stack direction="row" spacing={0.5}>
                                                                    {isPdf && (
                                                                        <IconButton size="small" color="primary" title={`View: ${docName}`}
                                                                            disabled={docLoading === cal.id}
                                                                            onClick={() => handleViewPdf(cal.calDoc, cal.id)}>
                                                                            {docLoading === cal.id ? <CircularProgress size={14} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                        </IconButton>
                                                                    )}
                                                                    <IconButton size="small" title="Download" disabled={docLoading === cal.id}
                                                                        onClick={() => handleDownload(cal.calDoc, cal.id, 'download')}>
                                                                        <DownloadIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Stack>
                                                            ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {/* Tool Docs */}
                    {toolDocs.length > 0 && (
                        <Accordion sx={accordionSx}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                                {sectionTitle(`Tool Documents (${toolDocs.length})`)}
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                                <Stack spacing={0.5}>
                                    {toolDocs.map((doc, idx) => {
                                        const path = doc.docLocation || '';
                                        const fileName = path.split(/[/\\]/).pop() || '—';
                                        const ext = fileName.split('.').pop()?.toLowerCase() || '';
                                        const isPdf = ext === 'pdf';
                                        return (
                                            <Card key={doc.id || idx} variant="outlined" sx={{ p: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip label={ext.toUpperCase() || 'FILE'} size="small"
                                                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: isPdf ? '#e53935' : '#757575', color: '#fff' }} />
                                                    <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }} noWrap>{fileName}</Typography>
                                                    <Stack direction="row" spacing={0.5}>
                                                        {isPdf && (
                                                            <IconButton size="small" color="primary" disabled={docLoading === doc.id}
                                                                onClick={() => handleViewPdf(doc.docLocation, doc.id)}>
                                                                {docLoading === doc.id ? <CircularProgress size={14} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                                                            </IconButton>
                                                        )}
                                                        <IconButton size="small" disabled={docLoading === doc.id}
                                                            onClick={() => handleDownload(doc.docLocation, doc.id, 'download')}>
                                                            <DownloadIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Stack>
                                                </Box>
                                            </Card>
                                        );
                                    })}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </>
            )}

            {!calSelectedPart && (
                <Card sx={{ p: 3, m: 1.5, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">Select a tool from the table to view calibration details</Typography>
                </Card>
            )}

            {/* PDF Preview Modal */}
            <PdfPreviewModal open={pdfPreviewOpen} onClose={() => { setPdfPreviewOpen(false); setPdfBlob(null); }}
                pdfBlob={pdfBlob} filename={pdfFilename} loading={pdfLoading} />

            {/* Camera Capture */}
            <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handlePhotoCapture} />
        </>
    );
}
